/**
 * @fileoverview The application's route tree.
 * @description Wires every page behind its guard: /login, /forgot-password and /reset-password
 * (the link Forgot Password emails) only render for signed-out visitors; /invitation/accept only
 * for a visitor with no session yet (via InvitationRoute — an already-signed-in visitor is sent
 * straight to the dashboard, the rest of that route's decision table lives inside
 * AcceptInvitationCard itself); /two-factor only for a
 * visitor with a pending post-credentials challenge (via TwoFactorRoute); the dashboard-area
 * routes only for signed-in ones (via ProtectedRoute), each additionally gated on its own
 * sales:*:* privilege (via RequirePrivilege — see nav-items.constants.ts for the
 * route-to-privilege mapping NavMenu uses to decide what to show, mirrored here to decide what to
 * actually allow); "/" resolves via RootRedirect and any unmatched path renders NotFoundPage.
 * /403 and /500 are plain, unguarded routes reached automatically by the Axios response
 * interceptor on a 403/5xx API response (see router-navigation.utils and response.interceptor.ts)
 * as well as by direct navigation, RequirePrivilege included. The dashboard-area routes
 * render DashboardShell rather than DashboardLayout directly, so they also register for push
 * notifications and resolve GET /identity/me before anything under them renders (see
 * DashboardShell). SettingsPage and ProfilePage keep their routes unguarded by any sales
 * privilege (every signed-in user can reach them) and absent from NAV_ITEMS, reachable via
 * DashboardHeaderUserMenu's dropdown. /change-password sits outside the dashboard shell
 * entirely, behind ForcePasswordChangeRoute: it is only for a user with mustChangePassword
 * (useVerifyTwoFactor and ProtectedRoute both send them there) and renders on AuthLayout like
 * login/two-factor, so no header, sidebar, or settings menu is shown during the forced change. A
 * voluntary change happens in SettingsPage's ChangePasswordModal instead. ReportsPage was a plain
 * demo stub, so it was dropped entirely (file left on disk, unrouted) when the sales module
 * replaced the dashboard-area content.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { createBrowserRouter } from 'react-router'

import DashboardShell from '@/app/routing/DashboardShell'
import RequirePrivilege from '@/app/routing/RequirePrivilege'
import RootRedirect from '@/app/routing/RootRedirect'
import AcceptInvitationPage from '@/pages/AcceptInvitationPage'
import ActiveClientsPage from '@/pages/ActiveClientsPage'
import AuditLogPage from '@/pages/AuditLogPage'
import BankingAssetQualityPage from '@/pages/BankingAssetQualityPage'
import BankingBankDetailsPage from '@/pages/BankingBankDetailsPage'
import BankingBranchAnalyticsIncomeSharePage from '@/pages/BankingBranchAnalyticsIncomeSharePage'
import BankingBranchAnalyticsPage from '@/pages/BankingBranchAnalyticsPage'
import BankingBranchAnalyticsSpreadCiIncomeMixPage from '@/pages/BankingBranchAnalyticsSpreadCiIncomeMixPage'
import BankingBusinessPerformancePage from '@/pages/BankingBusinessPerformancePage'
import BankingCapitalAdequacyPage from '@/pages/BankingCapitalAdequacyPage'
import BankingCustomerBasePage from '@/pages/BankingCustomerBasePage'
import BankingDepositsAveragesPage from '@/pages/BankingDepositsAveragesPage'
import BankingDepositsCostOfDepositPage from '@/pages/BankingDepositsCostOfDepositPage'
import BankingDepositsPage from '@/pages/BankingDepositsPage'
import BankingInvestmentsPage from '@/pages/BankingInvestmentsPage'
import BankingLoansInterestRateWisePage from '@/pages/BankingLoansInterestRateWisePage'
import BankingLoansPage from '@/pages/BankingLoansPage'
import BankingLoansProductWisePage from '@/pages/BankingLoansProductWisePage'
import BankingMembershipMemberCountPage from '@/pages/BankingMembershipMemberCountPage'
import BankingMembershipPage from '@/pages/BankingMembershipPage'
import BankingMembershipShareCapitalSegmentationPage from '@/pages/BankingMembershipShareCapitalSegmentationPage'
import BankingOverviewPage from '@/pages/BankingOverviewPage'
import BankingProductivityPage from '@/pages/BankingProductivityPage'
import BankingProfitabilityPage from '@/pages/BankingProfitabilityPage'
import CapabilitiesPage from '@/pages/CapabilitiesPage'
import ChangePasswordPage from '@/pages/ChangePasswordPage'
import ContactSupportPage from '@/pages/ContactSupportPage'
import DashboardPage from '@/pages/DashboardPage'
import ForbiddenPage from '@/pages/ForbiddenPage'
import ForgotPasswordPage from '@/pages/ForgotPasswordPage'
import LoginPage from '@/pages/LoginPage'
import MyWorkPage from '@/pages/MyWorkPage'
import NotFoundPage from '@/pages/NotFoundPage'
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage'
import ProfilePage from '@/pages/ProfilePage'
import ResetPasswordPage from '@/pages/ResetPasswordPage'
import SalesPerformancePage from '@/pages/SalesPerformancePage'
import SalesPipelinePage from '@/pages/SalesPipelinePage'
import SalesTeamPage from '@/pages/SalesTeamPage'
import ServerErrorPage from '@/pages/ServerErrorPage'
import SettingsPage from '@/pages/SettingsPage'
import TermsAndConditionsPage from '@/pages/TermsAndConditionsPage'
import TwoFactorPage from '@/pages/TwoFactorPage'
import UserManagementPage from '@/pages/UserManagementPage'
import { ROUTES } from '@/shared/constants/routes.constants'
import ForcePasswordChangeRoute from '@/shared/lib/auth/ForcePasswordChangeRoute'
import GuestRoute from '@/shared/lib/auth/GuestRoute'
import InvitationRoute from '@/shared/lib/auth/InvitationRoute'
import ProtectedRoute from '@/shared/lib/auth/ProtectedRoute'
import TwoFactorRoute from '@/shared/lib/auth/TwoFactorRoute'
import { setRouterNavigate } from '@/shared/lib/navigation/router-navigation.utils'

export const appRouter = createBrowserRouter([
  { path: ROUTES.ROOT, element: <RootRedirect /> },
  {
    element: <GuestRoute />,
    children: [
      { path: ROUTES.LOGIN, element: <LoginPage /> },
      { path: ROUTES.FORGOT_PASSWORD, element: <ForgotPasswordPage /> },
      { path: ROUTES.RESET_PASSWORD, element: <ResetPasswordPage /> },
    ],
  },
  {
    element: <InvitationRoute />,
    children: [{ path: ROUTES.ACCEPT_INVITATION, element: <AcceptInvitationPage /> }],
  },
  {
    element: <TwoFactorRoute />,
    children: [{ path: ROUTES.TWO_FACTOR, element: <TwoFactorPage /> }],
  },
  {
    element: <ForcePasswordChangeRoute />,
    children: [{ path: ROUTES.CHANGE_PASSWORD, element: <ChangePasswordPage /> }],
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <DashboardShell />,
        children: [
          {
            element: <RequirePrivilege privilege="sales:dashboard:view" />,
            children: [{ path: ROUTES.DASHBOARD, element: <DashboardPage /> }],
          },
          {
            element: <RequirePrivilege privilege="banking:overview:view" />,
            children: [{ path: ROUTES.BANKING_OVERVIEW, element: <BankingOverviewPage /> }],
          },
          {
            element: <RequirePrivilege privilege="banking:bank-details:view" />,
            children: [{ path: ROUTES.BANKING_BANK_DETAILS, element: <BankingBankDetailsPage /> }],
          },
          {
            element: <RequirePrivilege privilege="banking:membership:read" />,
            children: [
              { path: ROUTES.BANKING_MEMBERSHIP, element: <BankingMembershipPage /> },
              {
                path: ROUTES.BANKING_MEMBERSHIP_MEMBER_COUNT,
                element: <BankingMembershipMemberCountPage />,
              },
              {
                path: ROUTES.BANKING_MEMBERSHIP_SHARE_CAPITAL_SEGMENTATION,
                element: <BankingMembershipShareCapitalSegmentationPage />,
              },
            ],
          },
          {
            element: <RequirePrivilege privilege="banking:deposits:read" />,
            children: [
              { path: ROUTES.BANKING_DEPOSITS, element: <BankingDepositsPage /> },
              { path: ROUTES.BANKING_DEPOSITS_AVERAGES, element: <BankingDepositsAveragesPage /> },
            ],
          },
          {
            element: <RequirePrivilege privilege="banking:loans:read" />,
            children: [{ path: ROUTES.BANKING_LOANS, element: <BankingLoansPage /> }],
          },
          {
            element: <RequirePrivilege privilege="banking:branch-analytics:read" />,
            children: [
              { path: ROUTES.BANKING_BRANCH_ANALYTICS, element: <BankingBranchAnalyticsPage /> },
              {
                path: ROUTES.BANKING_BRANCH_ANALYTICS_SPREAD_CI_INCOME_MIX,
                element: <BankingBranchAnalyticsSpreadCiIncomeMixPage />,
              },
              {
                path: ROUTES.BANKING_BRANCH_ANALYTICS_INCOME_SHARE,
                element: <BankingBranchAnalyticsIncomeSharePage />,
              },
            ],
          },
          {
            element: <RequirePrivilege privilege="banking:customer-base:read" />,
            children: [
              { path: ROUTES.BANKING_CUSTOMER_BASE, element: <BankingCustomerBasePage /> },
            ],
          },
          {
            element: <RequirePrivilege privilege="banking:profitability:read" />,
            children: [
              { path: ROUTES.BANKING_PROFITABILITY, element: <BankingProfitabilityPage /> },
            ],
          },
          {
            element: <RequirePrivilege privilege="banking:business-performance:read" />,
            children: [
              {
                path: ROUTES.BANKING_BUSINESS_PERFORMANCE,
                element: <BankingBusinessPerformancePage />,
              },
            ],
          },
          {
            element: <RequirePrivilege privilege="banking:capital-adequacy:read" />,
            children: [
              { path: ROUTES.BANKING_CAPITAL_ADEQUACY, element: <BankingCapitalAdequacyPage /> },
            ],
          },
          {
            element: <RequirePrivilege privilege="banking:productivity:read" />,
            children: [{ path: ROUTES.BANKING_PRODUCTIVITY, element: <BankingProductivityPage /> }],
          },
          {
            element: <RequirePrivilege privilege="banking:loans:read" />,
            children: [
              { path: ROUTES.BANKING_LOANS_PRODUCT_WISE, element: <BankingLoansProductWisePage /> },
              {
                path: ROUTES.BANKING_LOANS_INTEREST_RATE_WISE,
                element: <BankingLoansInterestRateWisePage />,
              },
            ],
          },
          {
            element: <RequirePrivilege privilege="banking:investments:read" />,
            children: [{ path: ROUTES.BANKING_INVESTMENTS, element: <BankingInvestmentsPage /> }],
          },
          {
            element: <RequirePrivilege privilege="banking:asset-quality:read" />,
            children: [
              { path: ROUTES.BANKING_ASSET_QUALITY, element: <BankingAssetQualityPage /> },
            ],
          },
          {
            element: <RequirePrivilege privilege="banking:deposits:read" />,
            children: [
              {
                path: ROUTES.BANKING_DEPOSITS_COST_OF_DEPOSIT,
                element: <BankingDepositsCostOfDepositPage />,
              },
            ],
          },
          {
            element: <RequirePrivilege privilege="sales:clients:read" />,
            children: [{ path: ROUTES.SALES_CLIENTS, element: <ActiveClientsPage /> }],
          },
          {
            element: <RequirePrivilege privilege="sales:my-page:view" />,
            children: [{ path: ROUTES.SALES_MY_PAGE, element: <MyWorkPage /> }],
          },
          {
            element: <RequirePrivilege privilege="sales:pipeline:read" />,
            children: [{ path: ROUTES.SALES_PIPELINE, element: <SalesPipelinePage /> }],
          },
          {
            element: <RequirePrivilege privilege="sales:team:read" />,
            children: [{ path: ROUTES.SALES_TEAM, element: <SalesTeamPage /> }],
          },
          {
            element: <RequirePrivilege privilege="sales:performance:read" />,
            children: [{ path: ROUTES.SALES_PERFORMANCE, element: <SalesPerformancePage /> }],
          },
          {
            element: <RequirePrivilege privilege="sales:users:manage" />,
            children: [{ path: ROUTES.SALES_USERS, element: <UserManagementPage /> }],
          },
          {
            element: <RequirePrivilege privilege="capabilities:manage" />,
            children: [{ path: ROUTES.SALES_CAPABILITIES, element: <CapabilitiesPage /> }],
          },
          {
            element: <RequirePrivilege privilege="audit:logs:read" />,
            children: [{ path: ROUTES.AUDIT_LOG, element: <AuditLogPage /> }],
          },
          { path: ROUTES.SETTINGS, element: <SettingsPage /> },
          { path: ROUTES.PROFILE, element: <ProfilePage /> },
        ],
      },
    ],
  },
  { path: ROUTES.FORBIDDEN, element: <ForbiddenPage /> },
  { path: ROUTES.SERVER_ERROR, element: <ServerErrorPage /> },
  { path: ROUTES.PRIVACY_POLICY, element: <PrivacyPolicyPage /> },
  { path: ROUTES.TERMS_AND_CONDITIONS, element: <TermsAndConditionsPage /> },
  { path: ROUTES.CONTACT_SUPPORT, element: <ContactSupportPage /> },
  { path: '*', element: <NotFoundPage /> },
])

setRouterNavigate((to) => appRouter.navigate(to))
